import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface TimetableType {
  id: number | string;
  name: string;
  session: string;
  // NOTE: only present if you've added `status` to the SELECT list in the
  // existing GET /timetable-types route (see backend file) — otherwise
  // this will always be undefined and every row will show as "Inactive".
  status?: 'active' | 'inactive';
}

interface TimetableTypeFormState {
  name: string;
  session: string;
}

interface ManageTimetableTypesModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional — called after any successful add/edit/delete/set-active so
   * the parent (Timetable.tsx, wherever useTimetableTypes lives) can
   * refetch its own list/store. Wire this to whatever refresh function
   * useTimetableTypes exposes. */
  onChanged?: () => void;
}

const EMPTY_FORM: TimetableTypeFormState = { name: '', session: '' };

const inputStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%', padding: '9px 11px', borderRadius: '9px',
  border: '1.5px solid #e2e8f0', fontSize: '13.5px', color: '#1e293b',
  background: disabled ? '#f1f5f9' : 'white', outline: 'none', fontFamily: 'inherit',
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const ManageTimetableTypesModal: React.FC<ManageTimetableTypesModalProps> = ({
  open,
  onClose,
  onChanged,
}) => {
  const toast = useRef<Toast>(null);

  const [types, setTypes] = useState<TimetableType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // Add / edit inline panel
  const [formOpen, setFormOpen] = useState(false);
  const [editingType, setEditingType] = useState<TimetableType | null>(null);
  const [formData, setFormData] = useState<TimetableTypeFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Inline delete confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Set-active in flight
  const [activatingId, setActivatingId] = useState<number | string | null>(null);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/timetable-types');
      if (response.data.status === 'success') {
        setTypes(response.data.data || []);
      } else {
        setTypes([]);
      }
      console.log(response.data)
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch timetable types';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchTypes();
  }, [open, fetchTypes]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setTypes([]);
      setLoading(true);
      setFormOpen(false);
      setEditingType(null);
      setConfirmDeleteId(null);
    }, 400);
  };

  // ── Add / Edit ──────────────────────────────────────────────────────────
  const openAddForm = () => {
    setEditingType(null);
    setFormData(EMPTY_FORM);
    setFormOpen(true);
    setConfirmDeleteId(null);
  };

  const openEditForm = (type: TimetableType) => {
    setEditingType(type);
    setFormData({ name: type.name || '', session: type.session || '' });
    setFormOpen(true);
    setConfirmDeleteId(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingType(null);
    setFormData(EMPTY_FORM);
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Timetable type name is required.';
    if (formData.name.trim().length > 50) return 'Name cannot exceed 50 characters.';
    if (!formData.session.trim()) return 'Session is required.';
    if (formData.session.trim().length > 20) return 'Session cannot exceed 20 characters.';
    return null;
  };

  const handleSaveForm = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: validationError, life: 4000 });
      return;
    }

    setSaving(true);
    try {
      const payload = { name: formData.name.trim(), session: formData.session.trim() };

      // Editing requires PUT /timetable-types/{id} — this route does NOT
      // exist in your current backend yet. See
      // timetable_types_update_delete_backend.php — update_timetable_type()
      // + the PUT route need to be added before edit will work.
      const response = editingType
        ? await apiClient({ method: 'PUT', url: `/timetable-types/${editingType.id}`, data: payload })
        : await apiClient({ method: 'POST', url: '/timetable-types', data: payload });

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: editingType ? 'Timetable type updated.' : 'Timetable type added.',
          life: 3500,
        });
        closeForm();
        await fetchTypes();
        onChanged?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || `Failed to ${editingType ? 'update' : 'add'} timetable type`,
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || `Failed to ${editingType ? 'update' : 'add'} timetable type`,
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleConfirmDelete = async (id: number | string) => {
    setDeleting(true);
    try {
      // Requires DELETE /timetable-types/{id} — also does NOT exist yet,
      // same file as above (delete_timetable_type() + route). The backend
      // function blocks deletion if time_slots or timetable rows still
      // reference this type, and returns a clear error message — that
      // message is what will show up in the toast below if it's blocked.
      const response = await apiClient.delete(`/timetable-types/${id}`);
      if (response.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Timetable type deleted.', life: 3500 });
        setConfirmDeleteId(null);
        await fetchTypes();
        onChanged?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Cannot Delete',
          detail: response.data.message || 'Failed to delete timetable type',
          life: 6000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to delete timetable type',
        life: 5000,
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── Set Active ──────────────────────────────────────────────────────────
  const handleSetActive = async (id: number | string) => {
    setActivatingId(id);
    try {
      // Existing endpoint: POST /timetable-types/{id}/set-active
      const response = await apiClient.post(`/timetable-types/${id}/set-active`);
      if (response.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Activated', detail: `"${response.data.name}" is now active.`, life: 3500 });
        await fetchTypes();
        onChanged?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Failed to set timetable type active',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to set timetable type active',
        life: 5000,
      });
    } finally {
      setActivatingId(null);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '14px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: '24px',
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
          flexShrink: 0, border: '3px solid white',
        }}>
          <i className="pi pi-calendar" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
            Timetable Types
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            {types.length} type{types.length === 1 ? '' : 's'} configured
          </div>
        </div>
      </div>

      {!formOpen && (
        <button
          onClick={openAddForm}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
            padding: '9px 16px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
          }}
        >
          <i className="pi pi-plus" style={{ fontSize: '12px' }} />
          Add
        </button>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '620px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: mttMaskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: mttMaskFadeOut 0.4s ease !important; }
          @keyframes mttMaskFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes mttMaskFadeOut { from { opacity: 1; } to { opacity: 0; } }
          .p-dialog { animation: mttDialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: mttDialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes mttDialogOpen { from { opacity: 0; transform: scale(0.92) translateY(-20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes mttDialogClose { from { opacity: 1; transform: scale(1) translateY(0); } to { opacity: 0; transform: scale(0.88) translateY(25px); } }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241,245,249,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
        `}</style>

        {/* ── Inline Add / Edit form ── */}
        {formOpen && (
          <div style={{
            background: 'white', borderRadius: '16px', padding: '18px',
            border: '1px solid #dbeafe', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            marginBottom: '14px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '14px' }}>
              {editingType ? 'Edit Timetable Type' : 'New Timetable Type'}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  maxLength={50}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={saving}
                  placeholder="e.g., Regular, Exam, Ramadan"
                  style={inputStyle(saving)}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                  Session *
                </label>
                <input
                  type="text"
                  value={formData.session}
                  maxLength={20}
                  onChange={(e) => setFormData({ ...formData, session: e.target.value })}
                  disabled={saving}
                  placeholder="e.g., 2025-2026"
                  style={inputStyle(saving)}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={closeForm}
                disabled={saving}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#475569', fontSize: '13px', fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveForm}
                disabled={saving}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white', fontSize: '13px', fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : editingType ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {/* ── List ── */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '16px' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading timetable types...</span>
          </div>
        ) : types.length === 0 && !formOpen ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '10px' }}>
            <i className="pi pi-inbox" style={{ fontSize: '32px', color: '#cbd5e1' }} />
            <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}>No timetable types found</span>
          </div>
        ) : types.length > 0 ? (
          <div style={{
            background: 'white', borderRadius: '16px', padding: '8px',
            border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            {types.map((type, idx) => {
              const isActive = type.status === 'active';
              const isConfirming = confirmDeleteId === type.id;
              const isActivating = activatingId === type.id;

              return (
                <div
                  key={type.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '12px 10px',
                    borderBottom: idx === types.length - 1 ? 'none' : '1px solid #f1f5f9',
                  }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                    background: isActive ? '#f0fdf4' : '#f8fafc',
                    color: isActive ? '#166534' : '#94a3b8',
                    border: `1.5px solid ${isActive ? '#86efac' : '#e5e7eb'}`,
                  }}>
                    <i className="pi pi-calendar" style={{ fontSize: '14px' }} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#1f2937', marginBottom: '2px' }}>
                      {type.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                      Session: {type.session}
                    </div>
                  </div>

                  <span style={{
                    padding: '4px 12px', borderRadius: '7px',
                    border: `2px solid ${isActive ? '#86efac' : '#e5e7eb'}`,
                    background: isActive ? '#f0fdf4' : '#f8fafc',
                    color: isActive ? '#166534' : '#94a3b8',
                    fontSize: '11px', fontWeight: 700,
                    textTransform: 'capitalize', flexShrink: 0,
                  }}>
                    {type.status || 'unknown'}
                  </span>

                  {isConfirming ? (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        disabled={deleting}
                        style={{
                          padding: '6px 10px', borderRadius: '7px', border: '1px solid #e2e8f0',
                          background: 'white', color: '#475569', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        No
                      </button>
                      <button
                        onClick={() => handleConfirmDelete(type.id)}
                        disabled={deleting}
                        style={{
                          padding: '6px 10px', borderRadius: '7px', border: 'none',
                          background: '#dc2626', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        {deleting ? '…' : 'Delete'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {!isActive && (
                        <button
                          onClick={() => handleSetActive(type.id)}
                          disabled={isActivating}
                          title="Set Active"
                          style={{
                            width: '28px', height: '28px', borderRadius: '7px', border: '1px solid rgba(16,185,129,0.3)',
                            background: 'rgba(16,185,129,0.08)', color: '#059669',
                            cursor: isActivating ? 'not-allowed' : 'pointer', opacity: isActivating ? 0.6 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <i className={isActivating ? 'pi pi-spin pi-spinner' : 'pi pi-check'} style={{ fontSize: '11px' }} />
                        </button>
                      )}
                      <button
                        onClick={() => openEditForm(type)}
                        title="Edit"
                        style={{
                          width: '28px', height: '28px', borderRadius: '7px', border: '1px solid rgba(59,130,246,0.25)',
                          background: 'rgba(59,130,246,0.08)', color: '#2563eb', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <i className="pi pi-pencil" style={{ fontSize: '11px' }} />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(type.id)}
                        title="Delete"
                        style={{
                          width: '28px', height: '28px', borderRadius: '7px', border: '1px solid rgba(239,68,68,0.25)',
                          background: 'rgba(239,68,68,0.08)', color: '#dc2626', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <i className="pi pi-trash" style={{ fontSize: '11px' }} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default ManageTimetableTypesModal;
